import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { creditNoteService } from '../../../api/apiService';
import '../../../App.css';

function CreditNotesCancelled() {
  const navigate = useNavigate();
  const [creditNotes, setCreditNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return isNaN(date.getTime()) ? String(d) : date.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' });
  };

  const formatTime = (d) => {
    if (!d) return '';
    const date = typeof d === 'string' ? new Date(d) : d;
    if (isNaN(date.getTime())) return '';
    return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  };

  const formatMoney = (n) => {
    if (n == null || n === '') return '—';
    const num = typeof n === 'number' ? n : parseFloat(n);
    return isNaN(num) ? String(n) : num.toFixed(2);
  };

  const displayAgency = (name) => {
    const s = name != null ? String(name).trim() : '';
    return s.length <= 1 ? '—' : s;
  };

  const getClientName = (cn) => {
    if (cn.client_name) return cn.client_name;
    if (Array.isArray(cn.items) && cn.items[0]?.client_name) return cn.items[0].client_name;
    if (typeof cn.client === 'string' && cn.client.length > 1) return cn.client;
    return null;
  };

  const doFetch = useCallback(() => {
    setLoading(true);
    setError(null);
    creditNoteService.getCreditNotes({ limit: 1000, status: 'cancelled' })
      .then((res) => {
        const all = res.data?.creditNotes || [];
        setCreditNotes(all.filter(cn => cn.status === 'cancelled'));
      })
      .catch((err) => {
        console.error('Error loading cancelled credit notes:', err);
        setError(err.message || 'Failed to load cancelled credit notes');
        setCreditNotes([]);
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    doFetch();
  }, []);

  if (loading) {
    return (
      <div className="content-wrapper page">
        <div className="content-wrapper">
          <h1>Cancelled credit notes</h1>
          <p>Loading…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="content-wrapper page">
      <div className="content-wrapper">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 20 }}>
          <h1 style={{ margin: 0 }}>Cancelled credit notes</h1>
          <button
            type="button"
            className="button"
            style={{ fontSize: 12, padding: '4px 12px' }}
            onClick={() => navigate('/admin/credit-notes')}
          >
            ← Back to credit notes
          </button>
        </div>

        {error && (
          <div style={{ padding: 12, marginBottom: 16, background: '#f8d7da', color: '#721c24', borderRadius: 6 }}>
            {error}
          </div>
        )}

        {creditNotes.length === 0 ? (
          <p style={{ color: '#666' }}>No cancelled credit notes.</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #dee2e6', textAlign: 'left', background: '#f8f9fa' }}>
                  <th style={{ padding: '8px 6px' }}>Number</th>
                  <th style={{ padding: '8px 6px' }}>Type</th>
                  <th style={{ padding: '8px 6px' }}>Working Dates</th>
                  <th style={{ padding: '8px 6px' }}>Talent</th>
                  <th style={{ padding: '8px 6px' }}>Client</th>
                  <th style={{ padding: '8px 6px' }}>Agency</th>
                  <th style={{ padding: '8px 6px' }}>Net</th>
                  <th style={{ padding: '8px 6px' }}>Gross</th>
                  <th style={{ padding: '8px 6px' }}>Created</th>
                  <th style={{ padding: '8px 6px' }}>Cancelled</th>
                </tr>
              </thead>
              <tbody>
                {creditNotes.map(cn => (
                  <tr key={cn.id} style={{ borderBottom: '1px solid #eee', color: '#666' }}>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{cn.credit_note_number || cn.id}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{cn.invoice_type || '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>
                      {cn.booking_dates
                        ? cn.booking_dates.split(/\s*,\s*|\n/).filter(Boolean).map((d, i) => <div key={i}>{d.trim()}</div>)
                        : formatDate(cn.invoice_date)}
                    </td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{cn.talent_name || '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{getClientName(cn) || '—'}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{displayAgency(cn.agency_name)}</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{formatMoney(cn.net_amount)} €</td>
                    <td style={{ padding: '6px 6px', fontSize: 11 }}>{formatMoney(cn.gross_amount)} €</td>
                    <td style={{ padding: '6px 6px' }}>
                      <div style={{ fontSize: 11 }}>{cn.created_at ? `${formatDate(cn.created_at)} ${formatTime(cn.created_at)}` : '—'}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{cn.created_by || ''}</div>
                    </td>
                    <td style={{ padding: '6px 6px' }}>
                      <div style={{ fontSize: 11 }}>{cn.cancelled_at ? `${formatDate(cn.cancelled_at)} ${formatTime(cn.cancelled_at)}` : '—'}</div>
                      <div style={{ fontSize: 11, color: '#999' }}>{cn.cancelled_by || ''}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default CreditNotesCancelled;
